// @catalog F.010
/**
 * selectors.ts — Selectores derivados puros para ForecastPage rediseño.
 *
 * Provee:
 *   - getProductUrgency(p): clasifica urgencia de reposición (critical/warn/overstock/ok).
 *   - getQuickFilterCounts(products): counts pre-computados de los 6 chips quick filter.
 *   - applyQuickAndAdvancedFilters(products, quick, advanced): filtra por quick+advanced.
 *
 * Fuente literal: handoff `claude design/MxP_forecast_compras_unzipped/design_handoff_forecast_page/`
 *   README §Comportamiento §Urgencia líneas 205-211 (función urgencia exacta).
 *   README §1 Toolbar §Quick filters líneas 115-117 (6 chips + tones).
 *   README §Comportamiento §Side filters líneas 219-223 (intersección por categoría).
 *   AGENT_PROMPT.md Fase 2 líneas 54-57 + Fase 6 líneas 77-81.
 */
import { ProductData } from "./types";

export type Urgency = "critical" | "warn" | "overstock" | "ok";

export type QuickFilterValue =
  | "all"
  | "critical"
  | "warn"
  | "overstock"
  | "unclassified"
  | "inactive";

export interface AdvancedFilters {
  abc: Set<string>;
  xyz: Set<string>;
  demand: Set<string>;
  lifecycle: Set<string>;
  dci: Set<string>;
  activeOnly: boolean;
}

export interface QuickFilterCounts {
  all: number;
  critical: number;
  warn: number;
  overstock: number;
  unclassified: number;
  inactive: number;
}

/**
 * Clasifica urgencia de reposición.
 * Spec literal (README §Comportamiento §Urgencia líneas 205-211):
 *   if (totalInventory < safetyStock) → "critical"
 *   if (totalInventory < reorderPoint) → "warn"
 *   if (totalInventory > reorderPoint * 3) → "overstock"
 *   else → "ok"
 *
 * Campos null/undefined caen al default `?? 0` (semántica literal del handoff:
 * mapping vacío equivale a thresholds en 0 → cualquier stock positivo → "overstock").
 *
 * Mapeo de nombres (handoff usa p.totalInventory + p.mapping.safetyStock; ProductData
 * real usa p.totalAvailableQty + p.safetyStock + p.reorderPoint directos).
 */
export function getProductUrgency(p: ProductData): Urgency {
  const totalInventory = p.totalAvailableQty ?? 0;
  const safetyStock = p.safetyStock ?? 0;
  const reorderPoint = p.reorderPoint ?? 0;
  if (totalInventory < safetyStock) return "critical";
  if (totalInventory < reorderPoint) return "warn";
  if (totalInventory > reorderPoint * 3) return "overstock";
  return "ok";
}

/**
 * "Sin clasificar" en el handoff = producto SIN ABC classification asignado
 * (prototype/forecast-page.jsx línea 36: `!p.mapping.abcClassification`).
 * NO confundir con "unifiedCategory vacío" (filtro distinto del legacy
 * CategoryTreeFilter que vive en filter-and-sort.ts).
 */
export function isUnclassified(p: ProductData): boolean {
  return !p.abcClassification;
}

export function isInactive(p: ProductData): boolean {
  return p.activeForPurchase === false;
}

/**
 * Pre-computa counts para los 6 chips quick filter del header
 * (handoff README §1 Quick filters líneas 115-117).
 *
 * Counts NO son mutuamente excluyentes — un producto critical + inactive
 * cuenta en ambos. La mutex es a nivel de filtro aplicado, no de count.
 */
export function getQuickFilterCounts(
  products: ProductData[],
): QuickFilterCounts {
  const counts: QuickFilterCounts = {
    all: products.length,
    critical: 0,
    warn: 0,
    overstock: 0,
    unclassified: 0,
    inactive: 0,
  };
  for (const p of products) {
    const u = getProductUrgency(p);
    if (u === "critical") counts.critical += 1;
    else if (u === "warn") counts.warn += 1;
    else if (u === "overstock") counts.overstock += 1;
    if (isUnclassified(p)) counts.unclassified += 1;
    if (isInactive(p)) counts.inactive += 1;
  }
  return counts;
}

export function emptyAdvancedFilters(): AdvancedFilters {
  return {
    abc: new Set<string>(),
    xyz: new Set<string>(),
    demand: new Set<string>(),
    lifecycle: new Set<string>(),
    dci: new Set<string>(),
    activeOnly: false,
  };
}

export function hasActiveAdvancedFilters(f: AdvancedFilters): boolean {
  return (
    f.abc.size > 0 ||
    f.xyz.size > 0 ||
    f.demand.size > 0 ||
    f.lifecycle.size > 0 ||
    f.dci.size > 0 ||
    f.activeOnly
  );
}

export function countActiveAdvancedFilters(f: AdvancedFilters): number {
  return (
    f.abc.size +
    f.xyz.size +
    f.demand.size +
    f.lifecycle.size +
    f.dci.size +
    (f.activeOnly ? 1 : 0)
  );
}

/**
 * Aplica quick filter (mutex) + advanced filters (intersección por categoría).
 *
 * Quick filter (handoff README §Comportamiento §Quick filters líneas 214-217):
 *   uno solo activo a la vez; "all" resetea.
 *
 * Advanced filters (handoff README §Comportamiento §Side filters líneas 219-223):
 *   Producto pasa si está en TODOS los sets activos (intersección por categoría).
 *   Dentro de cada categoría: OR (cualquier valor del Set matchea).
 *   `activeOnly=true` agrega filtro activeForPurchase !== false.
 */
export function applyQuickAndAdvancedFilters(
  products: ProductData[],
  quickFilter: QuickFilterValue,
  advanced: AdvancedFilters,
): ProductData[] {
  let out = products;

  if (quickFilter !== "all") {
    out = out.filter((p) => {
      switch (quickFilter) {
        case "critical":
          return getProductUrgency(p) === "critical";
        case "warn": {
          // "Bajo reorder" incluye critical+warn (AGENT_PROMPT.md L96 + prototype L52).
          const u = getProductUrgency(p);
          return u === "warn" || u === "critical";
        }
        case "overstock":
          return getProductUrgency(p) === "overstock";
        case "unclassified":
          return isUnclassified(p);
        case "inactive":
          return isInactive(p);
        default:
          return true;
      }
    });
  }

  if (hasActiveAdvancedFilters(advanced)) {
    out = out.filter((p) => {
      if (advanced.abc.size > 0 && !advanced.abc.has(p.abcClassification ?? "")) return false;
      if (advanced.xyz.size > 0 && !advanced.xyz.has(p.xyzClassification ?? "")) return false;
      if (advanced.demand.size > 0 && !advanced.demand.has(p.demandClassification ?? "")) return false;
      if (advanced.lifecycle.size > 0 && !advanced.lifecycle.has(p.lifecycleStatus ?? "")) return false;
      if (advanced.dci.size > 0 && !advanced.dci.has(p.dciStatus ?? "")) return false;
      if (advanced.activeOnly && p.activeForPurchase === false) return false;
      return true;
    });
  }

  return out;
}
